import type { FunctionComponent, PropsWithChildren } from 'react';
import classNames from 'classnames';
import type { ICON } from '@george-labs.com/george-assets/icons';
import { Button, Dropdown, UnreadIndicator } from '@george-labs.com/design-system';

import { useDropdownMaxHeight } from '@shared/core-ui-layout';

import dockButtonStyles from './DevToolsDockButton.module.scss';

interface DevToolsDropdownProps extends PropsWithChildren {
    isOpen: boolean;
    toggleIcon: ICON;
    toggleLabel: string;
    showUnreadToggleIndicator?: boolean;
    showRecordingToggleIndicator?: boolean;
    indicatorLabel?: string;
    onOpen: () => void;
    onClose: () => void;
}

export const DevToolsDropdown: FunctionComponent<DevToolsDropdownProps> = ({
    isOpen,
    toggleIcon,
    toggleLabel,
    showUnreadToggleIndicator,
    showRecordingToggleIndicator,
    indicatorLabel,
    onOpen,
    onClose,
    children,
}) => {
    const maxHeight = useDropdownMaxHeight();

    const shouldShowIndicator = showUnreadToggleIndicator || showRecordingToggleIndicator;

    const indicatorClassName = classNames(dockButtonStyles.unreadIndicator, {
        [dockButtonStyles.recordingIndicator]: showRecordingToggleIndicator,
    });

    return (
        <Dropdown
            quick
            positionHorizontally
            padded={false}
            usePortal={false}
            alignment={Dropdown.ALIGNMENT.CENTER}
            isOpen={isOpen}
            maxHeight={maxHeight}
            onBeforeOpen={onOpen}
            arrow={false}
            onBeforeClose={onClose}
            toggle={
                // Temporary workaround, replace with DevToolsDockButton once GDS dropdowns accept custom toggles again
                <Button
                    className="position-relative justify-content-between rounded-0 g-fg-text-secondary"
                    icon={toggleIcon}
                    variant={Button.VARIANT.TERTIARY}
                    hiddenLabel={Button.HIDDEN_LABEL.ALWAYS}
                    fullWidth={Button.FULL_WIDTH.NEVER}
                    title={toggleLabel}
                    pill={
                        shouldShowIndicator && (
                            <UnreadIndicator
                                className={indicatorClassName}
                                label={indicatorLabel}
                            />
                        )
                    }
                >
                    {toggleLabel}
                </Button>
            }
        >
            {children}
        </Dropdown>
    );
};
